import { useState } from "react";
import { X, Download, Lock, CheckCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function BrochureGate({ type, onClose }) {
  const [formData, setFormData] = useState({ name: "", email: "", company: "" });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [downloadUrl, setDownloadUrl] = useState(null);

  const brochureConfig = {
    "golf-course": {
      title: "Golf Course Partnership Brochure",
      subtitle: "Championship-Level Tree Care",
      description: "Comprehensive guide to our golf course maintenance programs, pricing tiers, and service inclusions.",
      color: "#10B981",
    },
    "hotel-resort": {
      title: "Hotel & Resort Partnership Brochure",
      subtitle: "World-Class Grounds Maintenance",
      description: "Discover how we maintain Arizona's premier hospitality properties with our luxury service programs.",
      color: "#D4A853",
    },
    "residential": {
      title: "Residential Tree Care Guide",
      subtitle: "Protecting Arizona's Finest Homes",
      description: "Essential guide to residential tree care, seasonal tips, fire safety, and service pricing.",
      color: "#10B981",
    },
  };

  const config = brochureConfig[type] || brochureConfig["residential"];

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await axios.post(`${BACKEND_URL}/api/brochure-request`, {
        ...formData,
        brochure_type: type,
      });

      if (response.data.download_url) {
        setDownloadUrl(response.data.download_url);
        setIsSuccess(true);
        toast.success("Access granted! Your brochure is ready.");
      }
    } catch (error) {
      console.error("Error:", error);
      toast.error("Something went wrong. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDownload = () => {
    if (downloadUrl) {
      window.open(`${BACKEND_URL}${downloadUrl}`, "_blank");
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/70 backdrop-blur-sm"
        onClick={onClose}
      />
      
      {/* Modal */}
      <div className="relative bg-white w-full max-w-lg shadow-2xl" data-testid="brochure-gate-modal">
        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 p-2 hover:bg-gray-100 transition-colors z-10"
          data-testid="brochure-gate-close"
        >
          <X className="w-5 h-5 text-[#64748B]" />
        </button>

        {!isSuccess ? (
          <>
            {/* Header */}
            <div 
              className="p-8 text-center"
              style={{ backgroundColor: `${config.color}15` }}
            >
              <div 
                className="w-16 h-16 mx-auto mb-4 flex items-center justify-center"
                style={{ backgroundColor: config.color }}
              >
                <Lock className="w-8 h-8 text-white" />
              </div>
              <h2 className="font-['Cormorant_Garamond'] text-2xl font-bold text-[#0A1628] mb-2">
                {config.title}
              </h2>
              <p className="text-[#64748B]">{config.subtitle}</p>
            </div>

            {/* Form */}
            <div className="p-8">
              <p className="text-[#64748B] text-center mb-6">
                {config.description}
              </p>
              
              <p className="text-center text-sm text-[#0A1628] font-semibold mb-6">
                Enter your details to download the brochure
              </p>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-semibold text-[#0A1628] mb-1">
                    Full Name *
                  </label>
                  <Input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                    placeholder="John Smith"
                    className="border-[#10B981]/30 focus:border-[#10B981] rounded-none"
                    data-testid="brochure-name-input"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-[#0A1628] mb-1">
                    Email Address *
                  </label>
                  <Input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    required
                    placeholder="john@example.com"
                    className="border-[#10B981]/30 focus:border-[#10B981] rounded-none"
                    data-testid="brochure-email-input"
                  />
                </div>

                {(type === "golf-course" || type === "hotel-resort") && (
                  <div>
                    <label className="block text-sm font-semibold text-[#0A1628] mb-1">
                      Company/Property Name
                    </label>
                    <Input
                      type="text"
                      value={formData.company}
                      onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                      placeholder="Property or Company Name"
                      className="border-[#10B981]/30 focus:border-[#10B981] rounded-none"
                      data-testid="brochure-company-input"
                    />
                  </div>
                )}

                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full rounded-none py-5 uppercase tracking-[0.15em] text-sm font-bold"
                  style={{ 
                    background: `linear-gradient(to right, ${config.color}, ${config.color}dd)`,
                  }}
                  data-testid="brochure-submit-btn"
                >
                  {isSubmitting ? "Processing..." : "Access Brochure"}
                </Button>
              </form>

              <p className="text-center text-xs text-[#64748B] mt-4">
                We respect your privacy. Your information will only be used to 
                send you relevant tree care information.
              </p>
            </div>
          </>
        ) : (
          /* Success State */
          <div className="p-8 text-center">
            <div 
              className="w-20 h-20 mx-auto mb-6 flex items-center justify-center rounded-full"
              style={{ backgroundColor: `${config.color}20` }}
            >
              <CheckCircle className="w-10 h-10" style={{ color: config.color }} />
            </div>
            
            <h2 className="font-['Cormorant_Garamond'] text-2xl font-bold text-[#0A1628] mb-4">
              Your Brochure is Ready!
            </h2>
            
            <p className="text-[#64748B] mb-8">
              Thank you, {formData.name}! Click below to download your brochure. 
              We've also sent a copy to your email.
            </p>

            <Button
              onClick={handleDownload}
              className="w-full rounded-none py-5 uppercase tracking-[0.15em] text-sm font-bold mb-4"
              style={{ 
                background: `linear-gradient(to right, ${config.color}, ${config.color}dd)`,
              }}
              data-testid="brochure-download-btn"
            >
              <Download className="w-4 h-4 mr-2" />
              Download Brochure
            </Button>

            <Button
              onClick={onClose}
              variant="outline"
              className="w-full rounded-none py-4 text-sm"
            >
              Close
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
